import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getStorageItem, setStorageItem } from '../utils/storage';
import { validateTdsFileApi, handleValidationSuccess } from '../utils/tdsFileHandlers';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';

export function useTdsCommon({
    formName,
    defaultFormType,
    isCorrection = false,
    hasQ4Child = false,
    generateTdsStatementFile,
    validateDataForReturn,
    onImport,
    onValidationError
}) {
    const { user } = useAuth();
    const isInitialLoadedRef = useRef(false);
    const isValidatingRef = useRef(false);
    // 1. Common Form States
    const [profileData, setProfileDataState] = useState({
        section1: {
            taxDeduction: '',
            permanentAccountNumber: '',
            typeOfDeduction: '',
            isEarlierReturnFiled: 'No',
            receiptNoOriginal: '',
            receiptNoEarlier: ''
        },
        section2: {
            name: '',
            flatNo: '',
            nameOfPremises: '',
            roadStreet: '',
            areaLocality: '',
            townCity: '',
            pinCode: '',
            state: '',
            email: '',
            stdCode: '',
            telephoneNo: '',
            gstin: ''
        },
        section3: {
            sameAsAbove: false,
            name: '',
            designation: '',
            permanentAccountNumber: '',
            nameOfPremises: '',
            flatNo: '',
            areaLocality: '',
            roadStreet: '',
            pinCode: '',
            townCity: '',
            telephone: '',
            telephoneAlternate: '',
            state: '',
            email: '',
            emailAlternate: '',
            mobileNo: '',
            hasAddressChanged: 'N'
        }
    });

    const [challanData, setChallanDataState] = useState([]);
    const [tdsData, setTdsDataState] = useState([]);
    const [originalTdsData, setOriginalTdsDataState] = useState([]);
    const [csiFile, setCsiFile] = useState(null);
    const [consoFileContent, setConsoFileContent] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [showCsiUploadPopup, setShowCsiUploadPopup] = useState(false);
    const [showDownloadPrompt, setShowDownloadPrompt] = useState(false);

    const { activeClient } = useClient();

    // Selected Return Fields
    const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
    const [selectedQuarter, setSelectedQuarter] = useState('');
    const [selectedForm, setSelectedForm] = useState(defaultFormType);
    const [selectedReturnType, setSelectedReturnType] = useState(isCorrection ? 'Correction' : 'Regular');

    // Dynamic Key Generator
    const getStorageKey = useCallback((baseKey, specificFy, specificQtr) => {
        const clientId = activeClient ? activeClient._id : 'guest';
        const formPrefix = defaultFormType || formName || 'tds';
        if (baseKey === 'ProfileData') {
            return `${formPrefix}_${clientId}_${baseKey}`;
        }
        const fy = specificFy || selectedFinancialYear || 'default_fy';
        const qtr = specificQtr || selectedQuarter || 'default_qtr';
        return `${formPrefix}_${clientId}_${fy}_${qtr}_${baseKey}`;
    }, [activeClient, formName, defaultFormType, selectedFinancialYear, selectedQuarter]);

    // 2. Storage Sync Helpers
    const updateChallanData = useCallback((dataOrFn) => {
        setChallanDataState(prev => {
            const newData = typeof dataOrFn === 'function' ? dataOrFn(prev) : dataOrFn;
            if (newData) {
                const fy = selectedFinancialYear || '2026-27';
                const qtr = selectedQuarter || 'Q1';
                setStorageItem(getStorageKey('ChallanData', fy, qtr), newData).catch(err => console.error(err));
            }
            return newData;
        });
    }, [getStorageKey, selectedFinancialYear, selectedQuarter]);

    const updateTdsData = useCallback((dataOrFn) => {
        setTdsDataState(prev => {
            const newData = typeof dataOrFn === 'function' ? dataOrFn(prev) : dataOrFn;
            if (newData) {
                const fy = selectedFinancialYear || '2026-27';
                const qtr = selectedQuarter || 'Q1';
                setStorageItem(getStorageKey('TdsData', fy, qtr), newData).catch(err => console.error(err));
            }
            return newData;
        });
    }, [getStorageKey, selectedFinancialYear, selectedQuarter]);

    const updateOriginalTdsData = useCallback((dataOrFn) => {
        setOriginalTdsDataState(prev => {
            const newData = typeof dataOrFn === 'function' ? dataOrFn(prev) : dataOrFn;
            if (newData) {
                const fy = selectedFinancialYear || '2026-27';
                const qtr = selectedQuarter || 'Q1';
                setStorageItem(getStorageKey('OriginalTdsData', fy, qtr), newData).catch(err => console.error(err));
            }
            return newData;
        });
    }, [getStorageKey, selectedFinancialYear, selectedQuarter]);

    const updateConsoFileContent = useCallback((content) => {
        setConsoFileContent(content);
        if (content) {
            const fy = selectedFinancialYear || '2026-27';
            const qtr = selectedQuarter || 'Q1';
            setStorageItem(getStorageKey('ConsoFileContent', fy, qtr), content).catch(err => console.error(err));
        }
    }, [getStorageKey, selectedFinancialYear, selectedQuarter]);

    const updateProfileData = useCallback((dataOrFn) => {
        setProfileDataState(prev => {
            const newData = typeof dataOrFn === 'function' ? dataOrFn(prev) : dataOrFn;
            if (newData && typeof newData === 'object') {
                if (isInitialLoadedRef.current) {
                    setStorageItem(getStorageKey('ProfileData'), newData).catch(err => console.error(err));
                    const clientId = activeClient ? activeClient._id : 'guest';
                    setStorageItem(`client_${clientId}_ProfileData`, newData).catch(err => console.error(err));

                    const fy = selectedFinancialYear || '2026-27';
                    const qtr = selectedQuarter || 'Q1';
                    const receiptData = {
                        receiptNoOriginal: newData.section1?.receiptNoOriginal || '',
                        receiptNoEarlier: newData.section1?.receiptNoEarlier || '',
                        isEarlierReturnFiled: newData.section1?.isEarlierReturnFiled || 'No'
                    };
                    setStorageItem(getStorageKey('ReceiptDetails', fy, qtr), receiptData).catch(err => console.error(err));
                }
            }
            return newData;
        });
    }, [getStorageKey, activeClient, selectedFinancialYear, selectedQuarter]);

    const updateFinancialYear = useCallback((year) => {
        setSelectedFinancialYear(year);
        localStorage.setItem('tdsFinancialYear', year);
    }, []);

    const updateQuarter = useCallback((quarter) => {
        setSelectedQuarter(quarter);
        localStorage.setItem('tdsQuarter', quarter);
    }, []);

    const updateReturnType = useCallback(async (type) => {
        setSelectedReturnType(type);
        const fy = selectedFinancialYear || '2026-27';
        const qtr = selectedQuarter || 'Q1';
        await setStorageItem(getStorageKey('ReturnType', fy, qtr), type);
        localStorage.setItem('SELECTED_RETURN_TYPE', type);
    }, [getStorageKey, selectedFinancialYear, selectedQuarter]);

    // 3. Load data from localStorage/database on Mount or when Client/Period changes
    useEffect(() => {
        if (defaultFormType) {
            localStorage.setItem('tdsForm', defaultFormType);
        }
        (async () => {
            const savedFinancialYear = localStorage.getItem('tdsFinancialYear');
            const savedQuarter = localStorage.getItem('tdsQuarter');
            
            const fy = savedFinancialYear || '2026-27';
            const qtr = savedQuarter || 'Q1';

            if (savedFinancialYear && savedFinancialYear !== selectedFinancialYear) setSelectedFinancialYear(savedFinancialYear);
            if (savedQuarter && savedQuarter !== selectedQuarter) setSelectedQuarter(savedQuarter);

            const savedChallanData = await getStorageItem(getStorageKey('ChallanData', fy, qtr));
            const savedTdsData = await getStorageItem(getStorageKey('TdsData', fy, qtr));
            const savedOriginalTdsData = await getStorageItem(getStorageKey('OriginalTdsData', fy, qtr));
            const savedReceiptData = await getStorageItem(getStorageKey('ReceiptDetails', fy, qtr));
            const isProfileValid = (data) => {
                if (!data || typeof data !== 'object') return false;
                const parsed = typeof data === 'string' ? (function() { try { return JSON.parse(data); } catch(e) { return null; } })() : data;
                if (!parsed || typeof parsed !== 'object') return false;
                const s1 = parsed.section1 || {};
                const s2 = parsed.section2 || {};
                const s3 = parsed.section3 || {};
                return !!(s1.taxDeduction || s1.permanentAccountNumber || s2.name || s2.flatNo || s2.roadStreet || s2.areaLocality || s3.name || s3.designation);
            };

            let savedProfileData = await getStorageItem(getStorageKey('ProfileData'));
            if (!isProfileValid(savedProfileData)) {
                const clientId = activeClient ? activeClient._id : 'guest';
                const fallbackPrefix = (defaultFormType === '138' || formName === 'tds24Q') ? '24Q'
                    : (defaultFormType === '24Q') ? '138'
                    : (defaultFormType === '140' || formName === 'tds26Q') ? '26Q'
                    : (defaultFormType === '26Q') ? '140'
                    : (defaultFormType === '144' || formName === 'tds27Q') ? '27Q'
                    : (defaultFormType === '27Q') ? '144'
                    : (defaultFormType === '143' || formName === 'tds27EQ') ? '27EQ'
                    : (defaultFormType === '27EQ') ? '143'
                    : null;
                if (fallbackPrefix) {
                    const fallbackData = await getStorageItem(`${fallbackPrefix}_${clientId}_ProfileData`);
                    if (isProfileValid(fallbackData)) {
                        savedProfileData = fallbackData;
                    }
                }
                if (!isProfileValid(savedProfileData)) {
                    const clientMasterData = await getStorageItem(`client_${clientId}_ProfileData`);
                    if (isProfileValid(clientMasterData)) {
                        savedProfileData = clientMasterData;
                    }
                }
            }
            const cloudReturnType = await getStorageItem(getStorageKey('ReturnType', fy, qtr));
            const savedReturnType = cloudReturnType || localStorage.getItem('SELECTED_RETURN_TYPE');

            if (savedChallanData) setChallanDataState(savedChallanData);
            else setChallanDataState([]);

            if (savedTdsData) {
                const sanitized = savedTdsData.map(row => ({
                    ...row,
                    tdsAmount: (row.tdsAmount === undefined || row.tdsAmount === null || row.tdsAmount === '') ? 0 : row.tdsAmount
                }));
                setTdsDataState(sanitized);
            } else setTdsDataState([]);
            
            if (savedOriginalTdsData) {
                setOriginalTdsDataState(typeof savedOriginalTdsData === 'string' ? JSON.parse(savedOriginalTdsData) : savedOriginalTdsData);
            } else setOriginalTdsDataState([]);

            const savedConsoFileContent = await getStorageItem(getStorageKey('ConsoFileContent', fy, qtr));
            if (savedConsoFileContent) {
                setConsoFileContent(savedConsoFileContent);
            }
            
            let loadedProfile = null;
            if (savedProfileData) {
                try {
                    const parsed = typeof savedProfileData === 'string' ? JSON.parse(savedProfileData) : savedProfileData;
                    if (parsed && (parsed.section1 || parsed.section2 || parsed.section3)) {
                        loadedProfile = parsed;
                    }
                } catch (e) {
                    console.error('Error parsing profileData:', e);
                }
            }

            const targetReceiptNoOriginal = (savedReceiptData && savedReceiptData.receiptNoOriginal !== undefined) 
                ? savedReceiptData.receiptNoOriginal 
                : '';
            const targetReceiptNoEarlier = (savedReceiptData && savedReceiptData.receiptNoEarlier !== undefined) 
                ? savedReceiptData.receiptNoEarlier 
                : '';
            const targetIsEarlierReturnFiled = (savedReceiptData && savedReceiptData.isEarlierReturnFiled !== undefined) 
                ? savedReceiptData.isEarlierReturnFiled 
                : 'No';
            
            // Auto-fill active client details into profile (loadedProfile fields take precedence if saved)
            if (activeClient) {
                setProfileDataState(prev => ({
                    ...prev,
                    ...(loadedProfile ? loadedProfile : {}),
                    section1: { 
                        ...(prev.section1 || {}),
                        ...(loadedProfile ? loadedProfile.section1 : {}), 
                        receiptNoOriginal: targetReceiptNoOriginal,
                        receiptNoEarlier: targetReceiptNoEarlier,
                        isEarlierReturnFiled: targetIsEarlierReturnFiled,
                        taxDeduction: loadedProfile?.section1?.taxDeduction || activeClient.tan || prev.section1?.taxDeduction || '' 
                    },
                    section2: { 
                        ...(prev.section2 || {}),
                        ...(loadedProfile ? loadedProfile.section2 : {}), 
                        name: loadedProfile?.section2?.name || activeClient.name || prev.section2?.name || '' 
                    },
                    section3: {
                        ...(prev.section3 || {}),
                        ...(loadedProfile ? loadedProfile.section3 : {})
                    }
                }));
            } else if (loadedProfile) {
                setProfileDataState(prev => ({
                    ...prev,
                    ...loadedProfile,
                    section1: { 
                        ...(prev.section1 || {}), 
                        ...(loadedProfile.section1 || {}),
                        receiptNoOriginal: targetReceiptNoOriginal,
                        receiptNoEarlier: targetReceiptNoEarlier,
                        isEarlierReturnFiled: targetIsEarlierReturnFiled
                    },
                    section2: { ...(prev.section2 || {}), ...(loadedProfile.section2 || {}) },
                    section3: { ...(prev.section3 || {}), ...(loadedProfile.section3 || {}) }
                }));
            } else {
                setProfileDataState(prev => ({
                    ...prev,
                    section1: {
                        ...(prev.section1 || {}),
                        receiptNoOriginal: targetReceiptNoOriginal,
                        receiptNoEarlier: targetReceiptNoEarlier,
                        isEarlierReturnFiled: targetIsEarlierReturnFiled
                    }
                }));
            }
            
            const activeYear = savedFinancialYear || '2026-27';
            const isNewFormat = activeYear ? parseInt(activeYear.split('-')[0]) >= 2026 : false;
            let initialForm = defaultFormType;
            if (isNewFormat) {
                if (defaultFormType === '24Q') initialForm = '138';
                else if (defaultFormType === '26Q') initialForm = '140';
                else if (defaultFormType === '27Q') initialForm = '144';
                else if (defaultFormType === '27EQ') initialForm = '143';
            } else {
                if (defaultFormType === '138') initialForm = '24Q';
                else if (defaultFormType === '140') initialForm = '26Q';
                else if (defaultFormType === '144') initialForm = '27Q';
                else if (defaultFormType === '143') initialForm = '27EQ';
            }
            setSelectedForm(initialForm);
            localStorage.setItem('tdsForm', initialForm);
            
            if (isCorrection) {
                setSelectedReturnType('Correction');
                localStorage.setItem('SELECTED_RETURN_TYPE', 'Correction');
            } else {
                setSelectedReturnType('Regular');
                localStorage.setItem('SELECTED_RETURN_TYPE', 'Regular');
            }
            isInitialLoadedRef.current = true;
        })();
    }, [formName, defaultFormType, isCorrection, activeClient, getStorageKey]);

    // 4. Calculate Date Range for CSI File Download
    const challanDateRange = useMemo(() => {
        if (!challanData || challanData.length === 0) return { from: '', to: '' };
        const dates = challanData
            .map(c => c.dateOfDeposit)
            .filter(Boolean);
        if (dates.length === 0) return { from: '', to: '' };
        
        // Sort dates to find min and max
        const sorted = [...dates].sort();
        return {
            from: sorted[0],
            to: sorted[sorted.length - 1]
        };
    }, [challanData]);

    // 5. Unified Return File Generator
    const handlePrepareReturnFile = useCallback(async () => {
        const errors = validateDataForReturn ? validateDataForReturn() : [];
        if (errors && errors.length > 0) {
            if (onValidationError) {
                onValidationError(errors);
            } else {
                toast.error('Please fix validation errors before preparing return.');
            }
            return { success: false, errors };
        }

        try {
            const fileContent = generateTdsStatementFile();
            const blob = new Blob([fileContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            
            const fyParts = (selectedFinancialYear || '').split('-');
            const fyShort = (fyParts[1] || 'XX').slice(-2);
            let baseName = `${selectedForm || 'FORM'}${selectedQuarter || 'QX'}${fyShort}`;
            if (baseName.length > 8) baseName = baseName.slice(0, 8);
            
            link.href = url;
            link.download = `${baseName}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Return file generated successfully');
            return { success: true };
        } catch (error) {
            console.error('Error generating file:', error);
            toast.error('Failed to generate return file');
            return { success: false, error };
        }
    }, [validateDataForReturn, generateTdsStatementFile, selectedFinancialYear, selectedForm, selectedQuarter, onValidationError]);

    // 6. Unified CSI Validator
    const executeValidation = useCallback(async (currentCsiFile) => {
        if (isValidatingRef.current) return;
        isValidatingRef.current = true;
        setIsValidating(true);
        try {
            const fileContent = generateTdsStatementFile();
            const tan = profileData?.section1?.taxDeduction || 'UNKNOWN_TAN';
            const fyParts = (selectedFinancialYear || '').split('-');
            const fyShort = (fyParts[1] || 'XX').slice(-2);
            let baseName = `${selectedForm || 'FORM'}${selectedQuarter || 'QX'}${fyShort}`;
            if (baseName.length > 8) baseName = baseName.slice(0, 8);
            const fileName = `${baseName}.txt`;

            const reader = new FileReader();
            reader.onload = async (e) => {
                const csiFileContent = e.target.result;
                try {
                    const effectiveConso = consoFileContent || (await getStorageItem(getStorageKey('ConsoFileContent'))) || '';
                    const payload = {
                        fileContent,
                        fileName,
                        tan,
                        csiFileContent,
                        csiFileName: currentCsiFile.name,
                        consoFileContent: effectiveConso,
                        isRevised: isCorrection,
                        financialYear: selectedFinancialYear,
                        quarter: selectedQuarter,
                        formType: defaultFormType,
                        userType: user ? 'Premium' : 'Guest',
                        userName: user ? user.username : null
                    };

                    const data = await validateTdsFileApi(payload);
                    if (data.success) {
                        handleValidationSuccess(data, fileName);
                    } else {
                        toast.error(data.error || 'Validation failed');
                    }
                } catch (apiError) {
                    console.error('API Error:', apiError);
                    toast.error(apiError.response?.data?.error || 'Failed to connect to validation server.');
                } finally {
                    isValidatingRef.current = false;
                    setIsValidating(false);
                }
            };
            reader.onerror = () => {
                toast.error('Failed to read CSI file');
                isValidatingRef.current = false;
                setIsValidating(false);
            };
            reader.readAsDataURL(currentCsiFile);
        } catch (error) {
            console.error('Validation error:', error);
            toast.error(error.message || 'An error occurred');
            isValidatingRef.current = false;
            setIsValidating(false);
        }
    }, [generateTdsStatementFile, profileData, selectedFinancialYear, selectedForm, selectedQuarter, consoFileContent, isCorrection, defaultFormType, user, getStorageKey]);

    const handleValidateReturn = useCallback(() => {
        const errors = validateDataForReturn ? validateDataForReturn() : [];
        if (errors && errors.length > 0) {
            if (onValidationError) {
                onValidationError(errors);
            } else {
                toast.error('Please fix validation errors before validating.');
            }
            return;
        }
        setShowDownloadPrompt(true);
    }, [validateDataForReturn, onValidationError]);

    const handleSkipDownload = useCallback(() => {
        setShowDownloadPrompt(false);
        if (!csiFile) {
            setShowCsiUploadPopup(true);
        } else {
            executeValidation(csiFile);
        }
    }, [csiFile, executeValidation]);

    const handleCancelValidation = useCallback(() => {
        isValidatingRef.current = false;
        setIsValidating(false);
    }, []);

    // 7. Global Event Handlers
    useEffect(() => {
        if (hasQ4Child && selectedQuarter === 'Q4') {
            return;
        }
        const handleImport = (e) => {
            if (e.detail) {
                if (e.detail.challans && e.detail.challans.length > 0) {
                    updateChallanData(e.detail.challans);
                }
                const importedTds = e.detail.tds || e.detail.deductees;
                if (importedTds && importedTds.length > 0) {
                    if (isCorrection && originalTdsData && originalTdsData.length > 0) {
                        const hasCorruptedRecordNos = originalTdsData.length > 1 && originalTdsData.every(o => String(o.recordNo || '') === '1');
                        const seenOrigRecordNos = new Set();

                        const mergedTds = importedTds.map((importedRow, idx) => {
                            let orig = null;
                            if (idx < originalTdsData.length) {
                                orig = originalTdsData[idx];
                            }
                            if (!orig && importedRow.id) {
                                orig = originalTdsData.find(o => String(o.id) === String(importedRow.id));
                            }
                            if (!orig && importedRow.panOfDeductee) {
                                orig = originalTdsData.find(o => 
                                    (o.panOfDeductee || '').toUpperCase() === (importedRow.panOfDeductee || '').toUpperCase() &&
                                    Math.abs(parseFloat(o.amountOfPayment || 0) - parseFloat(importedRow.amountOfPayment || 0)) < 0.01
                                );
                            }

                            if (orig) {
                                let validRecordNo = orig.recordNo;
                                if (hasCorruptedRecordNos || !validRecordNo || seenOrigRecordNos.has(String(validRecordNo))) {
                                    validRecordNo = idx + 1;
                                }
                                seenOrigRecordNos.add(String(validRecordNo));

                                return {
                                    ...orig,
                                    ...importedRow,
                                    id: orig.id,
                                    recordNo: validRecordNo,
                                    source: orig.source || 'conso',
                                    originalPan: orig.originalPan || orig.panOfDeductee,
                                    originalTotalTax: orig.originalTotalTax !== undefined ? orig.originalTotalTax : (parseFloat(orig.tdsAmount || 0) + parseFloat(orig.surcharge || 0) + parseFloat(orig.educationCess || orig.cess || 0)),
                                    originalRefNo: orig.originalRefNo || orig.deducteeReferenceNo || '',
                                    originalSectionCode: orig.originalSectionCode || orig.sectionCode || ''
                                };
                            }
                            return importedRow;
                        });
                        updateTdsData(mergedTds);
                    } else {
                        updateTdsData(importedTds);
                    }
                }
                if (e.detail.originalTds) updateOriginalTdsData(e.detail.originalTds);
                if (typeof onImport === 'function') onImport(e.detail);
            }
        };

        const handlePrepareReturn = () => {
            handlePrepareReturnFile();
        };

        const handleValidateTds = () => {
            handleValidateReturn();
        };

        window.addEventListener('importTdsData', handleImport);
        window.addEventListener('prepareReturn', handlePrepareReturn);
        window.addEventListener('validateTdsFile', handleValidateTds);

        return () => {
            window.removeEventListener('importTdsData', handleImport);
            window.removeEventListener('prepareReturn', handlePrepareReturn);
            window.removeEventListener('validateTdsFile', handleValidateTds);
        };
    }, [
        hasQ4Child,
        selectedQuarter,
        updateChallanData,
        updateTdsData,
        updateOriginalTdsData,
        handlePrepareReturnFile,
        handleValidateReturn,
        onImport
    ]);

    return {
        // States
        profileData,
        challanData,
        tdsData,
        originalTdsData,
        csiFile,
        consoFileContent,
        isValidating,
        showCsiUploadPopup,
        showDownloadPrompt,
        selectedFinancialYear,
        selectedQuarter,
        selectedForm,
        selectedReturnType,
        challanDateRange,

        // Setters
        setProfileData: updateProfileData,
        setChallanData: updateChallanData,
        setTdsData: updateTdsData,
        setOriginalTdsData: updateOriginalTdsData,
        setCsiFile,
        setConsoFileContent: updateConsoFileContent,
        setIsValidating,
        setShowCsiUploadPopup,
        setShowDownloadPrompt,
        setSelectedFinancialYear: updateFinancialYear,
        setSelectedQuarter: updateQuarter,
        setSelectedForm,
        setSelectedReturnType: updateReturnType,

        // Handlers
        handlePrepareReturnFile,
        handleValidateReturn,
        handleSkipDownload,
        handleCancelValidation,
        executeValidation
    };
}
